import { CancelToken } from 'axios';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Segment, SegmentType, UserSegment } from '~src/interfaces/segment.interface';

import { getUserFullName } from '~src/interfaces/user.interface';
import SelectorHandler from '~src/modules/okrs/manageObjectives/shared/SelectorHandler';
import { OwnersSection } from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import PeopleChips
  from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/PeopleChips';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { ObjectiveItem, ObjectiveItemUpdate, ObjectiveStatus, OwnerI, } from '~src/store/okrs/interfaces';

interface OwnProps {
  hasChangePermissions: boolean;
  objective: ObjectiveItem;
  update: (values: Partial<ObjectiveItemUpdate>) => ApiReduxResp<ObjectiveItem>;
}

type Props = OwnProps;

const OwnersEdit: FC<Props> = props => {
  const typedSearch = (value: string, cancelToken: CancelToken) =>
    fetchSearchSegments(value, cancelToken, [SegmentType.User]);

  const [owners, setOwners] = useState(props.objective.owners || []);

  const changeOwners = (newOwners: Segment[]) => {
    if (newOwners.length > 0) {
      const ownersArr: OwnerI[] = newOwners.map(segmentOwner => {
        if (segmentOwner.type !== SegmentType.User) {
          return;
        }
        return ({
          uuid: segmentOwner.id,
          firstname: segmentOwner.meta && segmentOwner.meta.firstname ? segmentOwner.meta.firstname : 'UNKNOWN',
          lastname: segmentOwner.meta && segmentOwner.meta.lastname ? segmentOwner.meta.lastname : 'UNKNOWN',
          email: segmentOwner.meta && segmentOwner.meta.email ? segmentOwner.meta.email : 'UNKNOWN',
          imageUrl: segmentOwner.image_url || null,
        });
      }).filter((s): s is OwnerI => { return Boolean(s)});
      setOwners(ownersArr);
      props.update({ owners: ownersArr.map(owner => owner.uuid) });
    }
  };

  const [t] = useTranslation();

  const segmentOwners = useMemo(
    () =>
      owners.map(
        (owner: OwnerI): UserSegment => ({
          id: owner.uuid,
          type: SegmentType.User,
          meta: {
            firstname: owner.firstname,
            lastname: owner.lastname,
            email: owner.email,
            name: getUserFullName(owner),
            manager_uuid: null,
          },
          image_url: owner.imageUrl || '',
        }),
      ),
    [owners],
  );
  const isEnabled = props.hasChangePermissions && props.objective.status !== ObjectiveStatus.CLOSED;

  return (
    <OwnersSection>
      <SelectorHandler hoverTextKey="x.objective.add.icon.owners" iconName="iconPersonLock">
        {(_, select, blur) => (
          <PeopleChips
            blank
            minValue={1}
            locked={!isEnabled}
            fetchSuggestions={typedSearch}
            chipsName="owners"
            selectedPeople={segmentOwners}
            onChangePeople={changeOwners}
            onFocus={select}
            onBlur={blur}
            errorTxt={undefined}
            placeHolderKey="x.okrs.objective.owners.placeholder"
          />
        )}
      </SelectorHandler>
    </OwnersSection>
  );
};

export default OwnersEdit;
